import { SupportedLocale } from '@amzn/red-velvet-api';
import {
  DEFAULT_LOCALE,
  DEFAULT_LOCALE_BY_LANGUAGE,
  P0_SUPPORTED_LANGUAGES,
} from '../data/supportedLanguages';

const OFFERED_LOCALES: readonly SupportedLocale[] = P0_SUPPORTED_LANGUAGES.map(
  ({ locale }) => locale
);

/** Accepts the spellings browsers and PAPI actually send, such as `en_US` or `PT-br`. */
function normalizeTag(tag: string): string {
  return tag.trim().replace(/_/g, '-').toLowerCase();
}

function languageSubtagOf(tag: string): string {
  return normalizeTag(tag).split('-')[0];
}

function defaultLocaleForLanguage(languageSubtag: string): SupportedLocale | undefined {
  return Object.prototype.hasOwnProperty.call(DEFAULT_LOCALE_BY_LANGUAGE, languageSubtag)
    ? DEFAULT_LOCALE_BY_LANGUAGE[languageSubtag]
    : undefined;
}

/** The language the picker can show for this tag, or nothing if it offers none. */
export function toOfferedLocale(rawLocale: string | undefined): SupportedLocale | undefined {
  if (!rawLocale) {
    return undefined;
  }

  const normalizedTag = normalizeTag(rawLocale);

  return OFFERED_LOCALES.find((offered) => normalizeTag(offered) === normalizedTag);
}

/**
 * The best language for someone's browser settings.
 *
 * Each language the browser asks for is fully considered before moving to the next, so a user who
 * prefers French gets French even when the exact variant they asked for is unavailable and their
 * second choice is German. If they named another French variant further down the list, they get that
 * one rather than the default.
 */
export function matchBrowserLocale(
  browserLanguages: readonly string[]
): SupportedLocale | undefined {
  const requestedTags = browserLanguages.map(normalizeTag).filter((tag) => tag.length > 0);

  for (const tag of requestedTags) {
    const exactMatch = toOfferedLocale(tag);
    if (exactMatch) {
      return exactMatch;
    }

    const languageDefault = defaultLocaleForLanguage(languageSubtagOf(tag));
    if (!languageDefault) {
      continue;
    }

    const explicitlyRequestedVariant = requestedTags
      .map(toOfferedLocale)
      .find((offered) => offered && languageSubtagOf(offered) === languageSubtagOf(tag));

    return explicitlyRequestedVariant ?? languageDefault;
  }

  return undefined;
}

/**
 * The language the picker shows as current: the user's saved choice, else their browser's, else
 * English. A saved language the picker no longer offers is ignored so the dropdown is never blank.
 */
export function resolvePickerLocale(
  preferredLocale: string | undefined,
  browserLanguages: readonly string[]
): SupportedLocale {
  return toOfferedLocale(preferredLocale) ?? matchBrowserLocale(browserLanguages) ?? DEFAULT_LOCALE;
}
